<script>
import {Link, usePage} from "@inertiajs/vue3";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import IconFireFilled from "@js/components/Icons/IconFireFilled.vue";
import IconPercent from "@js/components/Icons/IconPercent.vue";
import SalesDepartmentPhone from "@js/components/Sections/Links/SalesDepartmentPhone.vue";
import TopBarCatalogDropdown from "@js/components/TopBar/Components/TopBarCatalogDropdown.vue";
import IconMenuToggle from "@js/components/Icons/IconMenuToggle.vue";
import IconDeliveryCar from "@js/components/Icons/IconDeliveryCar.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconUserCircle from "@js/components/Icons/IconUserCircle.vue";
import IconCart from "@js/components/Icons/IconCart.vue";
import IconSearch from "@js/components/Icons/IconSearch.vue";
import {useMenuStore} from "@js/store/MenuStore.js";
import IconX from "@js/components/Icons/IconX.vue";
import IconMapPoint from "@js/components/Icons/IconMapPoint.vue";
import SearchDropdown from "@js/components/Search/SearchDropdown.vue";
import {useSearchStore} from "@js/store/SearchStore.js";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCamera from "@js/components/Icons/IconCamera.vue";
import IconLogo from "@js/components/Icons/IconLogo.vue";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useCompareStore} from "@js/store/CompareStore.js";
import ModalSelect from "@js/components/Input/ModalSelect.vue";
import IconChevronDown from "@js/components/Icons/IconChevronDown.vue";
import IconDesigner from "@js/components/Icons/IconDesigner.vue";
import IconArchitecture from "@js/components/Icons/IconArchitecture.vue";
import {usePopupsControlStore} from "@js/store/PopupsControlStore.js";
import IconExcelColored from "@js/components/Icons/IconExcelColored.vue";
import {copyText, formatPhone} from "@js/helpers.ts";
import {contacts} from "@js/company_details.ts";
import {useThemeStore} from "@js/store/ThemeStore.js";
import IconPhone from "@js/components/Icons/IconPhone.vue";
import TopBarContactsBlock from "@js/components/TopBar/Components/TopBarContactsBlock.vue";
import IconMobileMenu from "@js/components/Icons/IconMobileMenu.vue";

export default {
    name: "TopBar",
    components: {
        IconMobileMenu,
        TopBarContactsBlock,
        IconPhone,
        IconExcelColored,
        IconArchitecture,
        IconDesigner,
        IconChevronDown,
        ModalSelect,
        IconScales,
        IconLogo,
        IconCamera,
        SearchDropdown,
        IconMapPoint,
        IconX,
        IconSearch,
        IconCart,
        IconUserCircle,
        IconHeart,
        IconDeliveryCar,
        IconMenuToggle,
        TopBarCatalogDropdown,
        SalesDepartmentPhone,
        IconPercent,
        IconFireFilled,
        OnlyMobile,
        OnlyDesktop,
        Link
    },
    data() {
        return {
            photoSearchTooltipVisible: false,
        }
    },
    setup() {
        const isSticky = ref(false)
        const searchInputRef = ref(null)
        const isClient = ref(false);

        const handleScroll = () => {
            const scrollY = window.scrollY
            isSticky.value = scrollY > 36
        }

        const page = usePage()

        const currentCategory = computed(() => {
            const url = page.url
            if (!url.startsWith('/catalog/')) return null

            const cleanUrl = url.split('?')[0]
            return cleanUrl.split('/').pop()
        })

        function isActive(category) {
            return currentCategory.value === category
        }

        const menuStore = useMenuStore()
        const searchStore = useSearchStore()
        const cartStore = useCartStore()
        const likeStore = useLikeStore()
        const compareStore = useCompareStore()
        const popupStore = usePopupsControlStore()

        onMounted(() => {
            isClient.value = true
            window.addEventListener("scroll", handleScroll)
            searchStore.startTypewriterEffect()
        })

        onBeforeUnmount(() => {
            window.removeEventListener("scroll", handleScroll)
        })

        return {
            isClient,
            isSticky,
            menuStore,
            popupStore,
            searchStore,
            likeStore,
            compareStore,
            cartStore,
            isActive,
            searchInputRef,
        }
    },
    methods: {
        copyText(text) {
            copyText(text);
            const themeStore = useThemeStore();
            themeStore.addToast("Телефон скопирован", "success");
        },
        showPhotoSearchTooltip() {
            this.photoSearchTooltipVisible = true;
        },
        hidePhotoSearchTooltip() {
            this.photoSearchTooltipVisible = false;
        },
    },
    computed: {
        formattedSalesDepartmentPhone() {
            return formatPhone(contacts.sales_department_phone);
        },
        formattedCommerceDepartmentPhone() {
            return formatPhone(contacts.commerce_department_phone);
        },
        formattedLogisticDepartmentPhone() {
            return formatPhone(contacts.logistics_department_phone);
        },
    },
}
</script>

<template>
    <header class="bg-white mb-8">
        <div class="bg-grayBg bg-[url('@img/patterns/dot.webp')] bg-[length:4px] pt-[8px] pb-[5px] rounded-b-full">
            <div class="container flex items-center justify-between text-sm text-black">
                <nav>
                    <ul class="inline-flex gap-5">
                        <li>
                            <div @click.prevent="popupStore.showCitySelectPopup"
                                 class="flex items-center gap-x-1 text-base md:hover:text-navHoverColor transition leading-tight cursor-pointer">
                                <IconMapPoint class="w-4 h-4 self-center text-navHoverColor -mt-0.5"/>
                                <span class="self-center text-sm">
                                {{ popupStore.selectedCity.name }}
                            </span>
                            </div>
                        </li>
                        <li>
                            <Link :href="route('contacts')"
                                  class="hover:text-navHoverColor leading-tight transition">
                                Контакты
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('deliveryAndShipping')"
                                  class="hover:text-navHoverColor leading-tight transition">
                                Доставка
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('aboutPayment')"
                                  class="hover:text-navHoverColor leading-tight transition">
                                Способы оплаты
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('priceList')"
                                  class="hover:text-navHoverColor leading-tight transition">
                                <div class="flex items-center gap-1">
                                    <IconExcelColored class="w-4 h-4 self-center text-navHoverColor"
                                                      third_color="#45ba4b" fourth_color="#fff"/>
                                    <span>
                                        Прайс-Лист
                                    </span>
                                </div>
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('aboutCompany')"
                                  class="hover:text-navHoverColor leading-tight transition">
                                О компании
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('comingSoon')"
                                  class="hover:text-navHoverColor leading-tight transition">
                                Калькулятор материалов
                            </Link>
                        </li>
                    </ul>
                </nav>
                <div>
                    <ul class="inline-flex gap-5">
                        <li class=" flex items-center leading-tight gap-1">
                            <ModalSelect :buttonClass="'group bg-transparent font-normal'" widthClass="w-[350px]"
                                         :closeOnClick="false">
                                <template #button>
                                    <div class="flex items-center gap-2 group-hover:text-navHoverColor transition">
                                        <div
                                            class="flex items-center gap-2 font-bold leading-tight text-black md:group-hover:text-navHoverColor">
                                            <div class="flex items-center justify-center w-5 h-5 rounded bg-baseColor">
                                                <IconPhone class="w-4 h-4 text-secondaryColor"/>
                                            </div>
                                            <SalesDepartmentPhone class="mt-0.5"/>
                                            <IconChevronDown class="w-3.5 h-3.5 text-black -ms-0.5"/>
                                        </div>
                                    </div>
                                </template>
                                <template #menu class="w-[350px]">
                                    <TopBarContactsBlock/>
                                </template>
                            </ModalSelect>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div :class="{'fixed top-0 right-0 left-0 border-b bg-white shadow z-10': isSticky, 'container': !isSticky}">
            <div class="mt-[16px] mb-[18px] flex flex-row items-center justify-between"
                 :class="{'container': isSticky}">
                <Link :href="route('home')" aria-label="Лого">
                    <IconLogo class="h-auto w-[150px] md:w-[240px]"/>
                </Link>
                <div class="inline-flex items-center h-[44px] mx-[18px] w-full">
                    <button @click.prevent="menuStore.toggleMenuCatalog"
                            class="h-[44px] me-[8px] min-w-fit px-[16px] bg-baseColor md:hover:bg-baseColor/80 rounded flex items-center gap-1 text-sm font-semibold transition leading-tight">
                        <IconMenuToggle v-if="!menuStore.isMenuCatalogOpen" class="w-5 h-5 rotate-180"/>
                        <IconX v-else class="w-5 h-5"/>
                        Каталог
                    </button>
                    <div class="flex flex-row h-[44px] relative w-full">
                        <div class="relative w-full" ref="searchInputRef">
                            <input type="search"
                                   v-model="searchStore.query"
                                   @click.prevent="searchStore.showDropdown"
                                   class="block p-2.5 ps-4 w-full z-20 text-sm text-gray-900  rounded border-baseColor border-2 focus:ring-0 focus:border-baseColor"
                                   :placeholder="searchStore.currentPlaceholder"
                                   autocomplete="off"/>
                            <button v-if="searchStore.query"
                                    @click.prevent="searchStore.clearQuery"
                                    type="button"
                                    class="absolute inset-y-0 end-[135px] flex items-center px-2 text-gray-400 md:hover:text-red-500"
                                    aria-label="Очистить поиск">
                                <IconX class="w-5 h-5"/>
                            </button>
                            <div class="absolute top-0 end-[80px] h-full">
                                <div class="relative h-full">
                                    <button type="button" aria-label="Поиск по фото"
                                            @click.prevent="searchStore.showImageSearch"
                                            @mouseenter="showPhotoSearchTooltip"
                                            @mouseleave="hidePhotoSearchTooltip"
                                            data-tooltip-placement="bottom"
                                            class="flex items-center justify-center h-full p-2.5 min-w-[45px] text-sm font-medium text-black bg-baseColor/40 rounded-e md:hover:bg-baseColor/60 focus:outline-none focus:ring-0 transition">
                                        <IconCamera class="w-5 h-5"/>
                                    </button>
                                    <div v-if="photoSearchTooltipVisible" role="tooltip"
                                         class="max-md:hidden absolute left-0 mt-2 z-10 inline-block px-3 py-3 text-sm font-medium text-nowrap text-white transition-opacity duration-300 bg-[#3a4250] rounded-lg shadow-xs">
                                        <div class="mb-1">
                                            Поиск по фото
                                        </div>
                                        <div class="text-xs">
                                            Загрузите фотографию и найдите товары
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Link type="submit" :href="route('searchPage', {search: searchStore.query})"
                                  class="flex items-center justify-center absolute top-0 end-0 h-full p-2.5 min-w-[80px] text-sm font-medium text-black bg-baseColor rounded-e border border-baseColor md:hover:bg-baseColor/80 focus:outline-none focus:ring-0 transition">
                                Найти
                            </Link>
                            <SearchDropdown v-if="searchInputRef" :inputRef="searchInputRef"/>
                        </div>
                    </div>
                </div>
                <nav class="inline-flex items-center justify-between min-w-[300px] gap-[12px] text-sm">
                    <Link :href="route('compareProducts')"
                          class="relative flex flex-col items-center md:hover:text-navHoverColor transition">
                        <IconScales class="w-[24px] h-[24px]"/>
                        <span>
                            Сравнение
                        </span>
                        <span v-if="isClient && compareStore.comparedIds.length"
                              class="absolute right-[17px] -top-[1px] flex items-center justify-center w-[16px] h-[16px]  bg-red-600 rounded-full text-xs font-semibold text-white">
                            {{ compareStore.comparedIds.length }}
                        </span>
                    </Link>
                    <Link :href="route('likedProducts')"
                          class="relative flex flex-col items-center md:hover:text-navHoverColor transition">
                        <IconHeart class="w-[24px] h-[24px]"/>
                        <span>
                            Избранное
                        </span>
                        <span v-if="isClient && likeStore.likedIds.length"
                              class="absolute right-[20px] -top-[1px] flex items-center justify-center w-[16px] h-[16px]  bg-red-600 rounded-full text-xs font-semibold text-white">
                            {{ likeStore.likedIds.length }}
                        </span>
                    </Link>
                    <Link :href="route('cart')"
                          class="relative flex flex-col items-center md:hover:text-navHoverColor transition">
                        <IconCart class="w-[24px] h-[24px]"/>
                        <span>
                            Смета
                        </span>
                        <span v-if="isClient && cartStore.items && cartStore.items.length"
                              class="absolute right-0 -top-[1px] flex items-center justify-center w-[16px] h-[16px]  bg-red-600 rounded-full text-xs font-semibold text-white">
                            {{ cartStore.items.length }}
                        </span>
                    </Link>
                    <div @click.prevent="menuStore.toggleProfileSidenav"
                         class="flex flex-col items-center md:hover:text-navHoverColor transition cursor-pointer">
                        <IconMobileMenu class="w-[24px] h-[24px]"/>
                        <span>
                            Меню
                        </span>
                    </div>
                </nav>
            </div>
            <TopBarCatalogDropdown/>
        </div>
        <nav class="container flex items-center justify-between gap-[5px] text-sm font-medium">
            <Link :href="route('catalog',{type_slug: 'pilomaterialy'})"
                  :class="['hover:text-navHoverColor transition', isActive('pilomaterialy') ? 'text-navHoverColor' : '']">
                Пиломатериалы
            </Link>
            <Link :href="route('catalog',{type_slug: 'trotuarnaya_plitka'})"
                  :class="['hover:text-navHoverColor transition', isActive('trotuarnaya_plitka') ? 'text-navHoverColor' : '']">
                Тротуарная плитка
            </Link>
            <Link :href="route('catalog',{type_slug: 'zhbi'})"
                  :class="['hover:text-navHoverColor transition', isActive('zhbi') ? 'text-navHoverColor' : '']">
                Железобетонные изделия
            </Link>
            <Link :href="route('catalog',{type_slug: 'beton'})"
                  :class="['hover:text-navHoverColor transition', isActive('beton') ? 'text-navHoverColor' : '']">
                Бетонные смеси
            </Link>
            <Link :href="route('comingSoon')"
                  :class="['hover:text-navHoverColor transition', isActive('comingSoon') ? 'text-navHoverColor' : '']">
                Нерудные материалы
            </Link>
            <Link :href="route('comingSoon')"
                  :class="['hover:text-navHoverColor transition', isActive('comingSoon') ? 'text-navHoverColor' : '']">
                Конструкции
            </Link>
            <Link :href="route('deliveryAndShipping')"
                  class="flex items-center gap-1 text-white text-xs bg-gradient-to-r from-[#2400ff] to-navHoverColor via-navHoverColor py-1 px-4 rounded-full font-bold">
                <IconFireFilled class="h-[18px] w-[18px]"/>
                Условия доставки
            </Link>
        </nav>
    </header>
</template>

<style lang="scss" scoped>
</style>
